import { useState, useEffect } from "react";
import Logo from "../../static/Logo.svg";
import DataTable from "../../utils/components/DataTable";
import ProjectsHeader from "./components/ProjectsHeader";
import ProjectsList from "./components/ProjectsList";
import NewProjectModal from "./components/NewProjectModal";
import AlertModal from "../../utils/components/AlertModal";
import API from "../../utils/API";
import Loading from "../../utils/components/Loading";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../utils/hooks/useCan";
import { confirmAlert } from "react-confirm-alert";
import { BiTrash } from "react-icons/bi";
import { MdRestoreFromTrash } from "react-icons/md";
import { useDispatch } from "react-redux";
import { setProjects as setDropdownProjects } from "../../utils/redux/app/appActions";

const SEARCH_TIMEOUT = 800;

const Projects = ({ translate }) => {
  const [isModalOn, setIsModalOn] = useState(false);
  const [isCardView, setCardView] = useState(true);
  const [selectedProject, setSelectedProject] = useState({});
  const [query, setQuery] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);

  const can = useCan();
  const dispatch = useDispatch();

  useEffect(() => {
    handleGetProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, showDeleted]);

  const handleSearch = (e) => {
    const query = e.target.value;
    setIsLoading(true);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query === searchedQuery) {
      setIsLoading(false);
      return;
    }

    setSearchTimeout(
      setTimeout(() => {
        setPage(1);
        setQuery(query);
      }, SEARCH_TIMEOUT)
    );
  };

  const handleGetProjects = () => {
    setIsLoading(true);
    API.getProjects({ query, deleted: showDeleted }).then(({ data: res }) => {
      setSearchedQuery(query);
      setProjects(res.data);
      setIsLoading(false);
    });
  };

  const handleRestoreProject = (project) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          message={translate("projects.restore_project.confirm")}
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("projects.restore_project.button"),
              onClick: () => {
                API.postData("/project/" + project.id + "/restore").then(() => {
                  onClose();
                  handleGetProjects();
                });
              },
              classNames: "btn btn-success",
            },
          ]}
        />
      ),
    });
  };

  const handleModal = (action) => {
    if (action === "success") {
      handleGetProjects();
    }
    setIsModalOn(false);
  };

  const handleDeleteProject = (project) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          message={translate("delete_confirmation")}
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                API.deleteData("/project/" + project.id).then(() => {
                  onClose();
                  handleGetProjects();
                  API.getProjects({ type: "dropdown" }).then(({ data: res }) => {
                    if (res.data) dispatch(setDropdownProjects(res.data));
                  });
                });
              },
              classNames: "btn btn-danger",
            },
          ]}
        />
      ),
    });
  };

  const columns = [
    {
      name: "#",
      selector: (row) => row.id,
      cell: () => <img width="70px" src={Logo} alt="Crane Logo" />,
      maxWidth: "90px",
    },
    {
      name: "Project Name".toLocaleUpperCase(),
      selector: (row) => row.name,
    },
    {
      name: "Created At".toLocaleUpperCase(),
      selector: (row) => row.created_at,
      format: (row) => timeStampToDateFormat(row.created_at),
      center: true,
    },
    {
      name: "Companies".toLocaleUpperCase(),
      selector: (row) => row.companies,
      compact: true,
      center: true,
    },
    {
      name: "Forms".toLocaleUpperCase(),
      selector: (row) => row.forms,
      compact: true,
      center: true,
    },
    ...(can.doThat("project:delete")
      ? [
          {
            name: "",
            cell: (row) =>
              showDeleted ? (
                <span
                  className="card-restore-btn"
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestoreProject(row);
                  }}
                >
                  <MdRestoreFromTrash size={18} />
                </span>
              ) : (
                <span
                  className="card-delete-btn"
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(row);
                  }}
                >
                  <BiTrash size={18} />
                </span>
              ),
            width: "50px",
            right: true,
          },
        ]
      : []),
  ];

  const handleGetProject = (project) => {
    let url = "/project/" + project.id;
    API.getData(url).then(({ data: res }) => {
      setSelectedProject({
        id: project.id,
        code: res.data.code,
        description: res.data.desc,
        logo: res.data.logo,
        name: res.data.name,
      });
      setIsModalOn(true);
    });
  };

  return (
    <div className="Projects">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Dinamikleştirilecek alan */}
            <ProjectsHeader
              translate={translate}
              handleSearch={handleSearch}
              showModal={() => {
                setSelectedProject({});
                setIsModalOn(true);
              }}
              setCardView={setCardView}
              canAdd={can.doThat("project:create") && !showDeleted}
              showDeleted={showDeleted}
              onShowDeletedChange={(val) => {
                setShowDeleted(val);
                setPage(1);
              }}
            />
            {/* Dinamikleştirilecek alan */}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {!isCardView &&
              (isLoading ? (
                <Loading
                  classNames="mt-3"
                  text={translate("loading") + `...`}
                />
              ) : (
                <DataTable
                  highlightOnHover={true}
                  pagination
                  onRowClicked={
                    !showDeleted
                      ? (project) => handleGetProject(project)
                      : undefined
                  }
                  data={projects}
                  columns={columns}
                />
              ))}
            {isCardView &&
              (isLoading ? (
                <Loading
                  classNames="mt-3"
                  text={translate("loading") + `...`}
                />
              ) : (
                <ProjectsList
                  onCardClicked={
                    !showDeleted
                      ? (project) => handleGetProject(project)
                      : undefined
                  }
                  onDeleteClicked={
                    !showDeleted && can.doThat("project:delete")
                      ? handleDeleteProject
                      : undefined
                  }
                  onRestoreClicked={
                    showDeleted && can.doThat("project:delete")
                      ? handleRestoreProject
                      : undefined
                  }
                  projects={projects}
                />
              ))}
          </div>
        </div>
      </div>
      {isModalOn && (
        <NewProjectModal
          translate={translate}
          show={isModalOn}
          selectedProject={selectedProject}
          hideModal={(action) => handleModal(action)}
        />
      )}
    </div>
  );
};

export default Projects;
